/** Small pieces the Brain's tabs and sub-pages share. */

import { useEffect, useState, type ReactNode } from 'react'
import { colorFor, monogram } from '@/core/utils/logo.resolver'
import type { DqSeverity, NodeCategory } from '../types/brain.types'

export const CATEGORY_COLOR: Record<NodeCategory, string> = {
  core: 'var(--core)',
  secondary: 'var(--secondary)',
  derived: 'var(--derived)',
}

export function CategoryBadge({ category }: { category: NodeCategory }) {
  return (
    <span className="cat-badge" data-c={category}>
      {category}
    </span>
  )
}

export function CategoryDot({ category }: { category: NodeCategory }) {
  return (
    <span
      className="cat-dot"
      style={{ background: CATEGORY_COLOR[category] ?? 'var(--core)' }}
      aria-hidden="true"
    />
  )
}

export function FlagChip({ flag }: { flag: string }) {
  return (
    <span className="flag" data-f={flag}>
      {flag}
    </span>
  )
}

export function SeverityChip({ severity }: { severity: DqSeverity }) {
  return (
    <span className="sev" data-s={severity}>
      {severity}
    </span>
  )
}

/** A horizontal tab strip with counts — the node detail page's five sub-pages. */
export function SubTabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { key: T; label: string; count?: number }[]
  value: T
  onChange: (key: T) => void
}) {
  return (
    <div className="subtabs" role="tablist" aria-label="Detail views"
      onKeyDown={(event) => {
        const index = tabs.findIndex((tab) => tab.key === value)
        const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
          : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
          : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : -1
        if (next < 0 || !tabs[next]) return
        event.preventDefault()
        onChange(tabs[next].key)
        event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
      }}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          className="subtab"
          role="tab"
          tabIndex={tab.key === value ? 0 : -1}
          aria-selected={tab.key === value}
          data-on={tab.key === value}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
          {tab.count !== undefined && <span className="subcount">{tab.count}</span>}
        </button>
      ))}
    </div>
  )
}

export function SectionHeader({
  title,
  hint,
  action,
}: {
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="row" style={{ alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <h3>{title}</h3>
        {hint && (
          <p className="small muted" style={{ margin: '3px 0 0', maxWidth: '74ch' }}>
            {hint}
          </p>
        )}
      </div>
      {action}
    </div>
  )
}

export function KeyValueGrid({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <div className="kvgrid">
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <div>{k}</div>
          <div>{v ?? <span className="faint">—</span>}</div>
        </div>
      ))}
    </div>
  )
}

/** Renders any stored value readably, without pretending JSON is a string. */
export function Value({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') return <span className="faint">—</span>
  if (typeof value === 'boolean') return <>{value ? 'yes' : 'no'}</>
  if (typeof value === 'object') return <code className="mono">{JSON.stringify(value)}</code>
  return <>{String(value)}</>
}

/** A comma-separated text input that edits a string array. */
export function ListInput({
  value,
  onChange,
  placeholder,
  id,
}: {
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  id?: string
}) {
  const [text, setText] = useState(value.join(', '))
  return (
    <input
      id={id}
      className="input"
      value={text}
      placeholder={placeholder}
      onChange={(e) => {
        setText(e.target.value)
        onChange(
          e.target.value
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean),
        )
      }}
    />
  )
}

/** Shows where a score falls against a rule's two thresholds. */
export function ThresholdBar({
  review,
  reviewMax = review,
  autoMerge,
  autoMergeMax = 1,
  score,
}: {
  review: number
  reviewMax?: number
  autoMerge: number
  autoMergeMax?: number
  score?: number
}) {
  const pct = (n: number) => `${Math.round(Math.min(1, Math.max(0, n)) * 100)}%`
  const span = (from: number, to: number) => ({
    left: pct(from),
    width: pct(Math.max(0, to - from)),
  })
  const f = (n: number) => n.toFixed(2)
  // Two lanes on one 0–1 scale, painted from the rule's own spans rather than
  // fixed stops, so the picture and the numbers can never disagree.
  return (
    <div className="thr">
      <div className="thr-lane" data-tone="merge">
        <span className="thr-lane-name">Merge</span>
        <div className="thr-track">
          <div className="thr-span" style={span(autoMerge, autoMergeMax)} />
          {score !== undefined && <div className="thr-score" style={{ left: pct(score) }} />}
        </div>
        <span className="thr-lane-range mono">
          {f(autoMerge)}–{f(autoMergeMax)}
        </span>
      </div>
      <div className="thr-lane" data-tone="review">
        <span className="thr-lane-name">Review</span>
        <div className="thr-track">
          <div className="thr-span" style={span(review, reviewMax)} />
          {score !== undefined && <div className="thr-score" style={{ left: pct(score) }} />}
        </div>
        <span className="thr-lane-range mono">
          {f(review)}–{f(reviewMax)}
        </span>
      </div>
      <div className="thr-scale" aria-hidden="true">
        <span />
        <div>
          <span>0</span>
          <span>0.25</span>
          <span>0.50</span>
          <span>0.75</span>
          <span>1</span>
        </div>
        <span className="mono">{score !== undefined ? `score ${f(score)}` : ''}</span>
      </div>
    </div>
  )
}

/** A JSON textarea that reports whether what is typed still parses. */
export function JsonField({
  value,
  onChange,
  rows = 5,
  label,
}: {
  value: unknown
  onChange: (next: unknown, valid: boolean) => void
  rows?: number
  label?: string
}) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2))
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="field">
      {label && <label className="field-label">{label}</label>}
      <textarea
        className="textarea textarea-mono"
        rows={rows}
        value={text}
        aria-invalid={error ? 'true' : undefined}
        onChange={(e) => {
          setText(e.target.value)
          try {
            const parsed = JSON.parse(e.target.value || '{}')
            setError(null)
            onChange(parsed, true)
          } catch (err) {
            setError((err as Error).message)
            onChange(undefined, false)
          }
        }}
      />
      {error && <p className="field-error">{error}</p>}
    </div>
  )
}

// --- control-plane pieces ----------------------------------------------------

/**
 * A tinted monogram tile. The colour is derived from the record's own id, so a
 * graph or a source looks the same every time it is drawn without anyone having
 * to pick one — and nothing is invented for the sake of decoration.
 */
export function Mark({ name, id, size = 26 }: { name: string; id: string; size?: number }) {
  const colour = colorFor(id)
  return (
    <span
      className="cp-mark"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        background: `${colour}22`,
        color: colour,
        fontSize: size <= 26 ? 11 : 13,
      }}
    >
      {monogram(name)}
    </span>
  )
}

export type ChipTone =
  | 'plain'
  | 'live'
  | 'ok'
  | 'warn'
  | 'err'
  | 'accent'
  | 'core'
  | 'secondary'
  | 'derived'

export function Chip({
  tone = 'plain',
  dot,
  children,
  title,
}: {
  tone?: ChipTone
  dot?: boolean
  children: ReactNode
  title?: string
}) {
  return (
    <span className="cp-chip" data-v={tone} title={title}>
      {dot && <span className="livedot" />}
      {children}
    </span>
  )
}

/** A share of something, 0–1. Amber below the halfway mark, so a bad number reads as one. */
export function Meter({ value }: { value: number }) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100)
  return (
    <div className="cp-meter" data-low={value < 0.5} role="img" aria-label={`${pct}%`}>
      <i style={{ width: `${pct}%` }} />
    </div>
  )
}

/** The search box every list screen carries, magnifier and all. */
export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
}) {
  return (
    <div className="cp-search">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <circle cx="7" cy="7" r="4.5" />
        <path d="m13.5 13.5-3-3" />
      </svg>
      <input
        className="input"
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

/**
 * A right-hand drawer. Used where a dialog would be the wrong shape — reading a
 * source's pipeline top to bottom, rather than filling in a form.
 */
export function Drawer({
  open,
  onClose,
  children,
}: {
  open: boolean
  onClose: () => void
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <>
      <div className="cp-scrim" data-on={open} onClick={onClose} />
      <aside className="cp-drawer" data-on={open} role="dialog" aria-modal="true" aria-hidden={!open}>
        {open && children}
      </aside>
    </>
  )
}

export function Field({
  label,
  help,
  children,
  required,
}: {
  label: string
  help?: string
  children: ReactNode
  required?: boolean
}) {
  return (
    <div className="field">
      <label className="field-label">
        {label}
        {required && <span className="field-req">*</span>}
      </label>
      {children}
      {help && <p className="field-help">{help}</p>}
    </div>
  )
}
